import { Box, Stack, styled } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { FormSchemaAccordionItem } from 'src/components/report-templates/FormSchemaAccordionItem'
import {
  ReportTemplateSearchBar,
  ReportTemplateSearchMode,
} from 'src/components/report-templates/ReportTemplateSearchBar'
//The location filter is hidden for this version. Will be available once the locations are clarified
//import { ReportTemplateLocationFilter } from 'src/components/report-templates/ReportTemplateLocationFilter'
//import { ReportTemplateFilteredLocationsChips } from 'src/components/report-templates/ReportTemplateFilteredLocationsChips'
import { ReportTemplateSummaryCards } from 'src/components/report-templates/ReportTemplateSummaryCards'
import {
  invalidateReportTemplate,
  useDeleteReportTemplate,
  useGetFormSchemaReportTemplates,
  usePreviewReport,
} from 'src/api/report-template'
import { IFormSchemaReportTemplateFilters, IReportTemplate } from 'src/generated-types/report-template'
import { ReportTemplateFilterBy, ReportTemplateLocation, ReportTemplateStatus } from 'src/enums'
import { Spinner } from 'src/components/Spinner'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { AddCircleOutline } from '@mui/icons-material'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import NoSystemReports from '../../../assets/svg/No-system-reports.svg'
import { CreateReportTemplateDialog } from 'src/components/report-templates/CreateReportTemplateDialog'
import { ReportDesignerContainer } from './ReportDesignerContainer'
import { IDropdownDto } from 'src/generated-types/api-result'
import { toast } from 'react-toastify'

const ReportGeneratorContainer = () => {
  const { t: translate } = useTranslation('dynamic-report')
  const { id: customerId } = useParams()
  const { showDialog, showConfirmDialog, openFilePreviewDialog } = useDialogCtx()

  const { mutateAsync: deleteTemplate } = useDeleteReportTemplate(customerId as EntityId)

  const [selectedStatus, setSelectedStatus] = useState<ReportTemplateStatus | null>(null)
  const [selectedLocations, setSelectedLocations] = useState<ReportTemplateLocation[]>([])
  const [searchMode, setSearchMode] = useState<ReportTemplateSearchMode>('schema')
  const [searchValue, setSearchValue] = useState('')
  const [expandedSchemaId, setExpandedSchemaId] = useState<number | null>(null)

  const queryOptions = useMemo<IFormSchemaReportTemplateFilters>(
    () => ({
      ...(selectedStatus !== null && { status: selectedStatus }),
      ...(searchValue.trim() && {
        searchText: searchValue.trim(),
        filterBy:
          searchMode === 'template' ? ReportTemplateFilterBy.TemplateName : ReportTemplateFilterBy.FormSchemaName,
      }),
      ...(selectedLocations.length > 0 && { locations: selectedLocations }),
    }),
    [selectedStatus, searchValue, searchMode, selectedLocations],
  )

  const { data: formSchemaTemplates, isLoading } = useGetFormSchemaReportTemplates(customerId as EntityId, queryOptions)
  const { mutateAsync: previewReport, isLoading: isPreviewLoading } = usePreviewReport(customerId as EntityId)

  useEffect(() => {
    setExpandedSchemaId(null)
  }, [selectedLocations, selectedStatus, searchValue, searchMode])

  useEffect(() => {
    if (expandedSchemaId === null && formSchemaTemplates && formSchemaTemplates.length > 0) {
      setExpandedSchemaId(formSchemaTemplates[0].formSchemaId)
    }
  }, [expandedSchemaId, formSchemaTemplates])

  const handleExpandChange = (schemaId: number, isExpanded: boolean) => {
    setExpandedSchemaId(isExpanded ? schemaId : null)
  }

  // The location filter is hidden for this version. Will be available once the locations are clarified
  // const handleLocationRemove = (location: ReportTemplateLocation) => {
  //   setSelectedLocations(prev => prev.filter(loc => loc !== location))
  // }

  const handleCreateTemplate = useCallback(
    async (selectedSchemaId?: number) => {
      await showDialog(
        CreateReportTemplateDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            formSchemaId: selectedSchemaId ?? undefined,
          },
          opts: {
            maxWidth: 'sm',
            fullWidth: true,
            PaperProps: {
              sx: {
                maxHeight: '90vh',
              },
            },
          },
        },
        undefined,
        true,
      )
    },
    [customerId, showDialog],
  )

  const handleEditTemplate = useCallback(
    async (selectedSchema?: IDropdownDto, template?: IReportTemplate) => {
      await showDialog(
        ReportDesignerContainer,
        {
          componentProps: {
            customerId: customerId as EntityId,
            formSchema: selectedSchema ?? undefined,
            templateId: template?.id ?? undefined,
          },
          opts: {
            maxWidth: 'xxl',
            fullWidth: true,
            PaperProps: {
              sx: {
                maxHeight: '90vh',
              },
            },
          },
        },
        undefined,
        true,
      )
    },
    [customerId, showDialog],
  )

  const handleDeleteTemplate = (template: IReportTemplate) => {
    showConfirmDialog(
      translate('delete-template', { ns: 'dynamic-report' }),
      translate('are-you-sure-delete-template', { ns: 'dynamic-report' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        await deleteTemplate(template.id as EntityId, {
          onSuccess: () => {
            toast.success(translate('template-deleted-successfully', { ns: 'dynamic-report' }))
            invalidateReportTemplate.getFormSchemaReportTemplates(customerId as EntityId)
            invalidateReportTemplate.getFormSchemaReportTemplateCounts(customerId as EntityId)
            invalidateReportTemplate.getReportTemplatesByFormSchemaVersion(
              customerId as EntityId,
              template.formSchemaVersionId,
            )
            pop()
          },
          onError: () => {
            toast.error(translate('error-deleting-template', { ns: 'dynamic-report' }))
          },
        })
      },
    )
  }
  const handleViewTemplate = async (template: IReportTemplate, formSchemaId: EntityId) => {
    await previewReport(
      {
        layout: template.layout,
        formSchemaVersionId: template.formSchemaVersionId,
        formSchemaId: formSchemaId,
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        IsFullReport: true,
      },
      {
        onSuccess: res => {
          const blob = new Blob([res.data as ArrayBuffer], { type: 'application/pdf' })
          const blobUrl = URL.createObjectURL(blob)
          openFilePreviewDialog(blobUrl, undefined, undefined, 'pdf')
        },
      },
    )
  }

  return (
    <CommonPageLayout
      titleSection={{
        title: translate('title'),
        subtitle: translate('subtitle'),
        subtitleFontSize: '14px',
      }}
      topAction={[
        <Box key="filters" display="flex" alignItems="center" gap={1}>
          {/* The location filter is hidden for this version. Will be available once the locations are clarified */}
          {/* <ReportTemplateLocationFilter
            selectedLocations={selectedLocations}
            onLocationsChange={setSelectedLocations}
          /> */}
          <ReportTemplateSearchBar
            searchMode={searchMode}
            searchValue={searchValue}
            onModeChange={setSearchMode}
            onSearchChange={setSearchValue}
          />
        </Box>,

        <GridActionButton
          key="add"
          startIcon={<AddCircleOutline />}
          buttonText={translate('create-template', { ns: 'dynamic-report' })}
          onClick={() => handleCreateTemplate()}
        />,
      ]}
    >
      <ContentStack>
        <ReportTemplateSummaryCards
          customerId={customerId as EntityId}
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
        />
        {/* The location filter is hidden for this version. Will be available once the locations are clarified */}
        {/* <ReportTemplateFilteredLocationsChips
          selectedLocations={selectedLocations}
          onLocationRemove={handleLocationRemove}
        /> */}
        <StyledBox className="text-center">
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Spinner />
            </Box>
          ) : formSchemaTemplates && formSchemaTemplates.length === 0 ? (
            <EmptyStatusCard
              image={NoSystemReports}
              header={translate('system-report-empty-status-title', { ns: 'document' })}
              content={translate('system-report-empty-status-content', { ns: 'document' })}
              imageWidth="162px"
              headerFontSize="18px"
              contentFontSize="14px"
            />
          ) : (
            <AccordionList>
              {(formSchemaTemplates ?? []).map(schema => (
                <FormSchemaAccordionItem
                  key={schema.formSchemaId}
                  schemaData={schema}
                  expanded={expandedSchemaId === schema.formSchemaId}
                  onExpandChange={isExpanded => handleExpandChange(schema.formSchemaId, isExpanded)}
                  onCreateTemplate={handleCreateTemplate}
                  onViewTemplate={(template: IReportTemplate) => handleViewTemplate(template, schema.formSchemaId)}
                  onEditTemplate={handleEditTemplate}
                  onDeleteTemplate={handleDeleteTemplate}
                  isPreviewLoading={isPreviewLoading}
                />
              ))}
            </AccordionList>
          )}
        </StyledBox>
      </ContentStack>
    </CommonPageLayout>
  )
}

export default ReportGeneratorContainer

const ContentStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1.5),
  width: '100%',
  paddingTop: theme.spacing(3),
}))

const AccordionList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
}))

const StyledBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.white.main,
  padding: theme.spacing(1),
  height: '100%',
}))
